/**
 * The document's one IndexedDB connection, and the stores that must be single.
 *
 * `recordDatabase()` in `@ff/store` is a **factory**, not a singleton: every
 * call returns a fresh handle that opens its own connection on first use. The
 * package says why the handle has to be shared — "every store in a transaction
 * has to come from the *same* connection or the transaction cannot span them",
 * and spanning stores is the whole reason this data lives in IndexedDB.
 *
 * Each stage-2a feature packet, unable to edit `app-state` (§6.4 gives it to
 * P05), reasonably memoised a handle of its own and wrote a comment promising
 * "one connection per document". Six of those promises are six connections:
 * `features/history`, `features/train/home`, `features/build/comboStudio`,
 * `features/build/comboLab`, `features/build/workouts` and `features/build/tape`
 * each held one. Two of them were worse than redundant — `workouts` and `tape`
 * held two different `PlanStorePort` instances over two connections to the same
 * object store, and `comboStudio` and `comboLab` did the same for combo
 * favorites — which is exactly the disagreement each of their comments warned
 * would happen "the moment either wrote". Three of those files asked, in
 * writing, for this module to exist.
 *
 * So the handle is created here, once, and the two ports that must not be
 * duplicated are instantiated here too. Ports built per-surface from this
 * handle (`createEffortStore`, `createReflectionStore`) may stay where they are:
 * they are stateless views over the connection, and it is the *connection* that
 * has to be shared. `createCheckpointStore` is not, because its coalescing
 * window is state — that one stays a single instance in `features/train/home`,
 * which is the only surface holding a ticker to build it with.
 *
 * Lazy on purpose. These accessors are reached from route modules the shell
 * loads dynamically, and opening IndexedDB at import time would open it before
 * the user has visited the surface that needs it.
 */

import type { PlanStorePort } from "@ff/domain/ports";
// `ComboFavoritesStorePort` is declared in `@ff/store` rather than beside the
// other ports in `@ff/domain`. Imported from where it actually lives; the
// misplacement is noted in the integration report, not papered over here.
import type { ComboFavoritesStorePort, RecordDatabase } from "@ff/store";
import { createComboFavoritesStore, createPlanStore, recordDatabase } from "@ff/store";

let database: RecordDatabase | undefined;
let planStore: PlanStorePort | undefined;
let comboFavorites: ComboFavoritesStorePort | undefined;

/**
 * The document's connection handle. Anything that needs a store built over the
 * app's real data takes it from here rather than calling `recordDatabase()`,
 * which would hand back a second, unrelated connection.
 */
export function browserRecordDatabase(): RecordDatabase {
  database ??= recordDatabase();
  return database;
}

/** The one plan library. Build's workouts tool and the Workout Tape share it. */
export function browserPlanStore(): PlanStorePort {
  planStore ??= createPlanStore(browserRecordDatabase());
  return planStore;
}

/** The one combo favorites library. Combo Studio and Combo Lab share it. */
export function browserComboFavorites(): ComboFavoritesStorePort {
  comboFavorites ??= createComboFavoritesStore(browserRecordDatabase());
  return comboFavorites;
}
